import { createMiddleware } from "@tanstack/react-start";
import { getAuth } from "@clerk/tanstack-react-start/server";
import { getWebRequest } from "@tanstack/react-start/server";
import type { UserSelect } from "~/lib/schemas";

export async function getUser(clerkUserId: string) {
  const { getUserByClerkId, persistUserFromClerkProfile } = await import(
    "~/lib/server/user"
  );

  const existingUser = await getUserByClerkId(clerkUserId);
  if (existingUser) {
    return existingUser;
  }

  const { initializeClerkIfNeeded, getClerk } = await import(
    "~/lib/clerk-server"
  );
  await initializeClerkIfNeeded();
  const clerk = getClerk();
  const clerkUser = await clerk.users.getUser(clerkUserId);

  return persistUserFromClerkProfile({
    clerkUserId,
    email:
      clerkUser.primaryEmailAddress?.emailAddress ??
      clerkUser.emailAddresses[0]?.emailAddress ??
      null,
    firstName: clerkUser.firstName ?? null,
    lastName: clerkUser.lastName ?? null,
  });
}

export const authMiddleware = createMiddleware({ type: "function" }).server<{
  user: UserSelect;
}>(async ({ next, context: _context }) => {
  try {
    const webRequest = getWebRequest();
    if (!webRequest) {
      throw new Error("No web request available");
    }

    const auth = await getAuth(webRequest);
    if (!auth?.userId) {
      throw new Error("Authentication required");
    }

    const user = await getUser(auth.userId);

    if (!user) {
      // This should not happen after auto-creation, but just in case
      throw new Error("User creation failed");
    }

    return next({
      context: {
        user,
      },
    });
  } catch (error) {
    // Server function middleware should throw errors, not redirects
    // Redirects are handled at the route level in beforeLoad
    if (error instanceof Error) {
      throw error;
    }
    throw new Error("Authentication failed");
  }
});
